import { zodResolver } from '@hookform/resolvers/zod';
import {
  adminUserSchema,
  createTenantSchema,
  okSchema,
  paginated,
  tenantSchema,
  type Tenant,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Building2, Globe, Loader2, Plus, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { EmptyState } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest } from '@/lib/api';
import { applyServerErrors, errorMessage } from '@/lib/forms';
import { useAuthenticatedSession } from '@/lib/session';

export const Route = createFileRoute('/_app/admin/customisation/tenants')({
  component: TenantsPage,
});

const tenantsQuery = {
  queryKey: ['studio', 'tenants', 'all'],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiRequest('/api/application/tenants', {
      schema: z.object({ tenants: z.array(tenantSchema) }),
      signal,
    }),
};

const adminsQuery = {
  queryKey: ['admin', 'users', 'admins-for-tenants'],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiRequest('/api/application/users?filter=admins&perPage=100', {
      schema: paginated(adminUserSchema),
      signal,
    }),
};

/**
 * Tenants: one per hosting provider sharing this install. Root administrators (assigned to no
 * tenant) manage the list; everyone else is scoped to the tenants they are assigned to.
 */
function TenantsPage() {
  const query = useQuery(tenantsQuery);
  const [creating, setCreating] = useState(false);
  return (
    <div className="mx-auto max-w-[1100px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Tenants</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Each tenant is a hosting provider with its own branding, dashboard and pages. A request
            resolves to a tenant by hostname, then by the customer’s tenant, then the default.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus /> New tenant
        </Button>
      </div>
      <QueryState query={query}>
        {(data) =>
          data.tenants.length === 0 ? (
            <EmptyState icon={Building2} title="No tenants">
              The default tenant is missing; run the migrations.
            </EmptyState>
          ) : (
            <div className="grid gap-4">
              {data.tenants.map((t) => (
                <TenantCard key={t.id} tenant={t} />
              ))}
            </div>
          )
        }
      </QueryState>
      <CreateTenantDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

const hostnamesText = (list: string[]) => list.join('\n');
const parseHostnames = (text: string) =>
  text
    .split(/[\n,\s]+/)
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);

function TenantCard({ tenant }: { tenant: Tenant }) {
  const queryClient = useQueryClient();
  const { user: me } = useAuthenticatedSession();
  const admins = useQuery(adminsQuery);
  const [name, setName] = useState(tenant.name);
  const [hosts, setHosts] = useState(hostnamesText(tenant.hostnames));
  const [selected, setSelected] = useState<Set<string>>(new Set(tenant.admins.map((a) => a.uuid)));
  const dirty =
    name !== tenant.name ||
    parseHostnames(hosts).join(',') !== tenant.hostnames.join(',') ||
    [...selected].sort().join(',') !==
      tenant.admins
        .map((a) => a.uuid)
        .sort()
        .join(',');
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['studio'] });
  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/tenants/${tenant.id}`, {
        method: 'PATCH',
        body: { name, hostnames: parseHostnames(hosts), adminUuids: [...selected] },
        schema: tenantSchema,
      }),
    onSuccess: async () => {
      await invalidate();
      toast.success('Tenant saved.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/tenants/${tenant.id}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: async () => {
      await invalidate();
      toast.success('Tenant deleted. Its customers now see the default branding.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const scopingSelf = selected.has(me.uuid);
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="text-muted-foreground size-4" aria-hidden /> {tenant.name}
            <code className="text-muted-foreground text-xs font-normal">{tenant.slug}</code>
          </CardTitle>
          {tenant.isDefault && <Badge variant="secondary">Default</Badge>}
          <Badge variant="outline">
            <Users /> {tenant.customerCount} customer{tenant.customerCount === 1 ? '' : 's'}
          </Badge>
          {!tenant.isDefault && (
            <Confirm
              title={`Delete ${tenant.name}?`}
              description="Its branding, pages, images and history are deleted. Customers assigned to it fall back to the default tenant. This can’t be undone."
              action="Delete tenant"
              destructive
              onConfirm={() => remove.mutate()}
            >
              <Button variant="ghost" size="sm" className="text-destructive ml-auto">
                <Trash2 /> Delete
              </Button>
            </Confirm>
          )}
        </div>
        <CardDescription>
          {tenant.hostnames.length
            ? `Served on ${tenant.hostnames.join(', ')}`
            : tenant.isDefault
              ? 'Served on every hostname that matches no other tenant.'
              : 'No hostnames: only its own customers see this tenant.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <FormField label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </FormField>
        <FormField
          label="Hostnames"
          description="One per line, e.g. panel.examplehosting.com. Point DNS and the reverse proxy at this panel; branding follows the Host header."
        >
          <Textarea value={hosts} onChange={(e) => setHosts(e.target.value)} rows={3} />
        </FormField>
        <div className="sm:col-span-2">
          <p className="mb-2 text-sm font-medium">Administrators</p>
          <p className="text-muted-foreground mb-3 text-xs">
            Administrators ticked here manage only the tenants they are assigned to. Administrators
            assigned to no tenant are root administrators and manage everything, including this
            list.
          </p>
          <QueryState query={admins}>
            {(d) => (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {d.data.map((u) => {
                  const id = `t${tenant.id}-${u.uuid}`;
                  return (
                    <div key={u.uuid} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={selected.has(u.uuid)}
                        onCheckedChange={(c) =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (c === true) next.add(u.uuid);
                            else next.delete(u.uuid);
                            return next;
                          })
                        }
                      />
                      <Label htmlFor={id} className="font-normal">
                        {u.username}
                        {u.uuid === me.uuid && (
                          <span className="text-muted-foreground"> (you)</span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </div>
            )}
          </QueryState>
          {scopingSelf && (
            <p className="text-warning mt-3 text-xs">
              You are assigning yourself to this tenant: you will stop being a root administrator
              and lose this Tenants page unless another root administrator remains.
            </p>
          )}
        </div>
        <div className="flex justify-end sm:col-span-2">
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" />} Save tenant
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const createForm = createTenantSchema.extend({ hostnamesText: z.string().default('') });
type CreateValues = z.input<typeof createForm>;

function CreateTenantDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const form = useForm<CreateValues>({
    resolver: zodResolver(createForm),
    defaultValues: { slug: '', name: '', hostnames: [], hostnamesText: '' },
  });
  const create = useMutation({
    mutationFn: (v: CreateValues) =>
      apiRequest('/api/application/tenants', {
        method: 'POST',
        body: { slug: v.slug, name: v.name, hostnames: parseHostnames(v.hostnamesText ?? '') },
        schema: tenantSchema,
      }),
    onSuccess: async (t) => {
      await queryClient.invalidateQueries({ queryKey: ['studio'] });
      toast.success(`Tenant ${t.name} created. Pick it in the studio’s tenant switcher.`);
      form.reset();
      onOpenChange(false);
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New tenant</DialogTitle>
          <DialogDescription>
            A separate provider brand on this install. Its branding starts from Hearth’s defaults.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => create.mutate(v))}
          noValidate
          className="grid gap-4"
        >
          <FormField label="Name" error={form.formState.errors.name?.message}>
            <Input {...form.register('name')} placeholder="Example Hosting" />
          </FormField>
          <FormField
            label="Slug"
            error={form.formState.errors.slug?.message}
            description="Used in the studio’s URLs; lowercase letters, numbers and dashes."
          >
            <Input {...form.register('slug')} placeholder="example-hosting" />
          </FormField>
          <FormField label="Hostnames (optional)" description="One per line.">
            <Textarea
              {...form.register('hostnamesText')}
              rows={2}
              placeholder="panel.examplehosting.com"
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? <Loader2 className="animate-spin" /> : <Globe />} Create tenant
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
